import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { StyleSheet, Platform, Dimensions, TextInput, ScrollView } from 'react-native';
import { StyledModal, StyledModalHandle, Text, View } from '@constants/Themed';
import styles from '@stylesheet';
import { Button, XStack, YStack, H4, H6, Separator } from 'tamagui';
import parseByDelimiter from '@utils/parseByDelimiter';
import SvgQRCode from 'react-native-qrcode-svg';
import * as Clipboard from 'expo-clipboard';
import * as FileSystem from 'expo-file-system';
import { AntDesign, MaterialIcons, Feather } from '@expo/vector-icons';
import * as Sharing from 'expo-sharing';
import { HOST } from '@api/constant';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

export type CalendarQRProps = {
  calendar: any;
}

export type CalendarQRHandle = {
  showModal: (calendarId: string) => void;
}

const CalendarQRModal = forwardRef<CalendarQRHandle, CalendarQRProps>((props, ref) => {
  const { calendar } = props;
  const [qrValue, setQrValue] = useState('');
  const modalRef = useRef<StyledModalHandle>(null);
  const qrCodeRef = useRef<any>(null);
  const [copyStatus, setCopyStatus] = useState('');
  const [downloadStatus, setDownloadStatus] = useState('');
  const [embedCode, setEmbedCode] = useState('');
  const [embedWidth, setEmbedWidth] = useState('100%');
  const [embedHeight, setEmbedHeight] = useState('600');
  const [showEmbedOptions, setShowEmbedOptions] = useState(false);
  const [embedCopyStatus, setEmbedCopyStatus] = useState('');
  const theme = themeSelector();
  const { width } = Dimensions.get('window');
  const isTablet = width > 768;

  const linkVal = __DEV__ ? `${HOST}` : 'https://api.smartsaas.pro';

  useImperativeHandle(ref, () => ({
    showModal(calendarId) {
      const generatedLink = `${linkVal}/calendar/bookings?id=${calendarId}`;
      setQrValue(generatedLink);
      generateEmbedCode(generatedLink, embedWidth, embedHeight);
      modalRef.current?.openModal();
    }
  }));

  const generateEmbedCode = (linkUrl: string, width: string, height: string) => {
    const code = `<iframe 
  src="${linkUrl}" 
  width="${width}" 
  height="${height}px" 
  frameborder="0" 
  scrolling="auto"
  style="border: none; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
</iframe>`;
    setEmbedCode(code);
  };

  const updateEmbedDimensions = (width: string, height: string) => {
    setEmbedWidth(width);
    setEmbedHeight(height);
    generateEmbedCode(qrValue, width, height);
  };

  const copyEmbedCode = async () => {
    try {
      await Clipboard.setStringAsync(embedCode);
      setEmbedCopyStatus('Embed code copied to clipboard!');
      setTimeout(() => setEmbedCopyStatus(''), 3000);
    } catch (error) {
      setEmbedCopyStatus('Failed to copy embed code');
      setTimeout(() => setEmbedCopyStatus(''), 3000);
    }
  };

  const copyToClipboard = async () => {
    try {
      await Clipboard.setStringAsync(qrValue);
      setCopyStatus('Link copied to clipboard!');
      setTimeout(() => setCopyStatus(''), 3000);
    } catch (error) {
      setCopyStatus('Failed to copy link');
      setTimeout(() => setCopyStatus(''), 3000);
    }
  };

  const shareLink = async () => {
    try {
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(qrValue, {
          dialogTitle: `Share ${parseByDelimiter(calendar?.title || calendar?.name || 'Calendar')}`
        });
      } else {
        copyToClipboard();
      }
    } catch (error) {
      copyToClipboard();
    }
  };

  const downloadQRCode = async () => {
    try {
      setDownloadStatus('Generating QR code...');

      if (Platform.OS === 'web') {
        // Web download using canvas
        const QRCode = require('qrcode');
        const qrDataURL = await QRCode.toDataURL(qrValue, {
          width: 512,
          margin: 2,
          color: {
            dark: '#333333',
            light: '#FFFFFF'
          }
        });

        const downloadLink = document.createElement('a');
        downloadLink.href = qrDataURL;
        downloadLink.download = `${calendar?.title || calendar?.name || 'Calendar'}_QR_${Date.now()}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        setDownloadStatus('QR code downloaded successfully!');
      } else {
        // Mobile download using react-native-qrcode-svg
        if (qrCodeRef.current) {
          qrCodeRef.current.toDataURL((dataURL: string) => {
            const filename = `${calendar?.title || calendar?.name || 'Calendar'}_QR_${Date.now()}.png`;

            if (Platform.OS === 'ios' || Platform.OS === 'android') {
              const base64Data = dataURL.replace('data:image/png;base64,', '');

              if (Sharing.isAvailableAsync()) {
                const fileUri = `${(FileSystem.Paths as any).documentDirectory}${filename}`;
                FileSystem.writeAsStringAsync(fileUri, base64Data, {
                  encoding: 'base64' as any,
                }).then(() => {
                  Sharing.shareAsync(fileUri, {
                    dialogTitle: 'Save QR Code',
                    mimeType: 'image/png'
                  });
                  setDownloadStatus('QR code shared successfully!');
                }).catch(() => {
                  setDownloadStatus('Failed to save QR code');
                });
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus('Failed to download QR code');
    }

    setTimeout(() => setDownloadStatus(''), 3000);
  };

  useEffect(() => {
    if (qrValue) {
      generateEmbedCode(qrValue, embedWidth, embedHeight);
    }
  }, [qrValue, embedWidth, embedHeight]);


  return (
    <StyledModal
      ref={modalRef}
      title={`Share ${parseByDelimiter(calendar?.title || calendar?.name || 'Calendar')}`}
      caption='Share your calendar with anyone to allow bookings and appointments'
      infoChildren={(
        <YStack style={styles.centerEverything}>
          <View style={stylesLocal.iconContainer}>
            <MaterialIcons name="event-available" size={32} color="#5ac0ea" />
          </View>

          <H4 style={{
            textAlign: 'center',
            marginTop: 16,
            marginBottom: 8,
            color: theme ? '#333' : '#fff',
            fontWeight: 'bold'
          }}>
            Share Your Calendar
          </H4>

          <Text style={{
            textAlign: 'center',
            color: '#8e99af',
            fontSize: 16,
            lineHeight: 22,
            paddingHorizontal: 20
          }}>
            Scan the QR code or share the link below to allow others to book appointments
          </Text>
          <Separator backgroundColor={theme ? '#e0e0e0' : '#404040'} marginBottom={24} />

          {/* Information Section */}
          <View style={stylesLocal.infoSection}>
            <H6 style={{
              marginBottom: 12,
              color: '#5ac0ea',
              fontWeight: 'bold'
            }}>
              How to use this calendar link:
            </H6>
            <YStack gap={8}>
              <View style={stylesLocal.infoItem}>
                <Feather name="smartphone" size={16} color="#8e99af" />
                <Text style={stylesLocal.infoText}>
                  Scan the QR code with any mobile device
                </Text>
              </View>
              <View style={stylesLocal.infoItem}>
                <Feather name="download" size={16} color="#8e99af" />
                <Text style={stylesLocal.infoText}>
                  Download high-quality QR code for printing or sharing
                </Text>
              </View>
              <View style={stylesLocal.infoItem}>
                <Feather name="calendar" size={16} color="#8e99af" />
                <Text style={stylesLocal.infoText}>
                  Recipients can view availability and book appointments
                </Text>
              </View>
              <View style={stylesLocal.infoItem}>
                <Feather name="users" size={16} color="#8e99af" />
                <Text style={stylesLocal.infoText}>
                  Anyone with this link can access your booking page
                </Text>
              </View>
              <View style={stylesLocal.infoItem}>
                <Feather name="code" size={16} color="#8e99af" />
                <Text style={stylesLocal.infoText}>
                  Embed calendar directly on your website
                </Text>
              </View>
            </YStack>
          </View>
        </YStack>
      )}
    >
      <ScrollView style={[theme ? styles.profileCard : styles.profileCardDark, { margin: 16, marginTop: 8 }]}>
        {/* QR Code Section */}
        <View style={[stylesLocal.qrContainer, { backgroundColor: theme ? '#f8f9fa' : '#383838' }]}>
          <View style={stylesLocal.qrCodeWrapper}>
            <SvgQRCode
              ref={qrCodeRef}
              size={isTablet ? 220 : 180}
              value={qrValue}
              backgroundColor="white"
              color="#333"
              logoMargin={8}
              logoBorderRadius={8}
              getRef={(c) => (qrCodeRef.current = c)}
            />
          </View>

          {/* Link Preview */}
          <View style={stylesLocal.linkPreview}>
            <H6 style={{
              marginBottom: 8,
              color: '#5ac0ea',
              fontWeight: 'bold',
              textAlign: 'center'
            }}>
              Booking Link
            </H6>
            <Text style={{
              fontSize: 12,
              color: theme ? '#666' : '#ccc',
              textAlign: 'center',
              paddingHorizontal: 16,
              lineHeight: 18,
              fontFamily: Platform.OS === 'web' ? 'monospace' : 'System'
            }}>
              {qrValue.length > 60 ? `${qrValue.substring(0, 60)}...` : qrValue}
            </Text>
          </View>
        </View>

        {/* Status Messages */}
        {copyStatus ? (
          <View style={[stylesLocal.statusContainer, {
            backgroundColor: copyStatus.includes('Failed') ? '#ffe6e6' : '#e8f5e8'
          }]}>
            <Feather
              name={copyStatus.includes('Failed') ? "alert-circle" : "check-circle"}
              size={16}
              color={copyStatus.includes('Failed') ? '#e74c3c' : '#27ae60'}
            />
            <Text style={{
              color: copyStatus.includes('Failed') ? '#e74c3c' : '#27ae60',
              fontSize: 14,
              fontWeight: '500',
              marginLeft: 8
            }}>
              {copyStatus}
            </Text>
          </View>
        ) : null}

        {downloadStatus ? (
          <View style={[stylesLocal.statusContainer, {
            backgroundColor: downloadStatus.includes('Failed') ? '#ffe6e6' : downloadStatus.includes('Generating') ? '#fff3cd' : '#e8f5e8'
          }]}>
            <Feather
              name={downloadStatus.includes('Failed') ? "alert-circle" : downloadStatus.includes('Generating') ? "download-cloud" : "check-circle"}
              size={16}
              color={downloadStatus.includes('Failed') ? '#e74c3c' : downloadStatus.includes('Generating') ? '#856404' : '#27ae60'}
            />
            <Text style={{
              color: downloadStatus.includes('Failed') ? '#e74c3c' : downloadStatus.includes('Generating') ? '#856404' : '#27ae60',
              fontSize: 14,
              fontWeight: '500',
              marginLeft: 8
            }}>
              {downloadStatus}
            </Text>
          </View>
        ) : null}

        {/* Action Buttons */}
        <XStack flexWrap='wrap' style={{ marginTop: 24, gap: 8, flexWrap: 'wrap' }}>
          <Button
            flex={1}
            size="$4"
            backgroundColor="#5ac0ea"
            color="white"
            minWidth={150}
            borderRadius={8}
            fontWeight="600"
            pressStyle={{ backgroundColor: '#4aa3d3' }}
            onPress={shareLink}
            icon={<AntDesign name="share-alt" size={18} color="white" />}
          >
            Share
          </Button>

          <Button
            flex={1}
            size="$4"
            minWidth={150}
            backgroundColor={theme ? '#f8f9fa' : '#404040'}
            color={theme ? '#333' : '#fff'}
            borderWidth={1}
            borderColor={theme ? '#e0e0e0' : '#555'}
            borderRadius={8}
            fontWeight="600"
            pressStyle={{
              backgroundColor: theme ? '#e9ecef' : '#4a4a4a'
            }}
            onPress={copyToClipboard}
            icon={<Feather name="copy" size={18} color={theme ? '#333' : '#fff'} />}
          >
            Copy
          </Button>

          <Button
            flex={1}
            size="$4"
            minWidth={150}
            backgroundColor="#28a745"
            color="white"
            borderRadius={8}
            fontWeight="600"
            pressStyle={{ backgroundColor: '#218838' }}
            onPress={downloadQRCode}
            disabled={downloadStatus.includes('Generating')}
            icon={<Feather name="download" size={18} color="white" />}
          >
            {downloadStatus.includes('Generating') ? 'Generating...' : 'Download QR'}
          </Button>
        </XStack>

        {/* Embed Code Section */}
        <View style={[stylesLocal.embedSection, { backgroundColor: theme ? '#f8f9fa' : '#383838' }]}>
          <Button
            size="$3"
            backgroundColor="transparent"
            color={theme ? '#333' : '#fff'}
            borderWidth={1}
            borderColor={theme ? '#e0e0e0' : '#555'}
            borderRadius={6}
            fontWeight="500"
            pressStyle={{ backgroundColor: theme ? '#e9ecef' : '#4a4a4a' }}
            onPress={() => setShowEmbedOptions(!showEmbedOptions)}
            icon={<Feather name="code" size={16} color={theme ? '#333' : '#fff'} />}
            style={{ marginBottom: showEmbedOptions ? 16 : 0 }}
          >
            {showEmbedOptions ? 'Hide Embed Options' : 'Show Embed Code'}
          </Button>

          {showEmbedOptions && (
            <YStack gap={16}>
              {/* Dimension Controls */}
              <XStack gap={12} alignItems="center">
                <View style={{ flex: 1 }}>
                  <Text style={[stylesLocal.labelText, { color: theme ? '#333' : '#fff' }]}>
                    Width
                  </Text>
                  <TextInput
                    style={[stylesLocal.dimensionInput, {
                      backgroundColor: theme ? '#fff' : '#2a2a2a',
                      borderColor: theme ? '#ddd' : '#555',
                      color: theme ? '#333' : '#fff'
                    }]}
                    value={embedWidth}
                    onChangeText={(value) => updateEmbedDimensions(value, embedHeight)}
                    placeholder="100%"
                    placeholderTextColor={theme ? '#999' : '#666'}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[stylesLocal.labelText, { color: theme ? '#333' : '#fff' }]}>
                    Height (px)
                  </Text>
                  <TextInput
                    style={[stylesLocal.dimensionInput, {
                      backgroundColor: theme ? '#fff' : '#2a2a2a',
                      borderColor: theme ? '#ddd' : '#555',
                      color: theme ? '#333' : '#fff'
                    }]}
                    value={embedHeight}
                    onChangeText={(value) => updateEmbedDimensions(embedWidth, value)}
                    placeholder="600"
                    placeholderTextColor={theme ? '#999' : '#666'}
                    keyboardType="numeric"
                  />
                </View>
              </XStack>

              {/* Quick Size Presets */}
              <View>
                <Text style={[stylesLocal.labelText, { color: theme ? '#333' : '#fff', marginBottom: 8 }]}>
                  Quick Presets
                </Text>
                <XStack gap={8} flexWrap="wrap">
                  <Button
                    size="$2"
                    backgroundColor={theme ? '#e9ecef' : '#404040'}
                    color={theme ? '#333' : '#fff'}
                    borderRadius={6}
                    onPress={() => updateEmbedDimensions('100%', '400')}
                  >
                    Mobile (100% × 400px)
                  </Button>
                  <Button
                    size="$2"
                    backgroundColor={theme ? '#e9ecef' : '#404040'}
                    color={theme ? '#333' : '#fff'}
                    borderRadius={6}
                    onPress={() => updateEmbedDimensions('100%', '600')}
                  >
                    Standard (100% × 600px)
                  </Button>
                  <Button
                    size="$2"
                    backgroundColor={theme ? '#e9ecef' : '#404040'}
                    color={theme ? '#333' : '#fff'}
                    borderRadius={6}
                    onPress={() => updateEmbedDimensions('800', '600')}
                  >
                    Desktop (800px × 600px)
                  </Button>
                </XStack>
              </View>

              {/* Embed Code Display */}
              <View>
                <Text style={[stylesLocal.labelText, { color: theme ? '#333' : '#fff', marginBottom: 8 }]}>
                  Embed Code
                </Text>
                <View style={[stylesLocal.codeContainer, {
                  backgroundColor: theme ? '#2a2a2a' : '#1a1a1a',
                  borderColor: theme ? '#ddd' : '#444'
                }]}>
                  <Text style={stylesLocal.codeText}>
                    {embedCode}
                  </Text>
                </View>
              </View>

              {/* Embed Status Message */}
              {embedCopyStatus ? (
                <View style={[stylesLocal.statusContainer, {
                  backgroundColor: embedCopyStatus.includes('Failed') ? '#ffe6e6' : '#e8f5e8'
                }]}>
                  <Feather
                    name={embedCopyStatus.includes('Failed') ? "alert-circle" : "check-circle"}
                    size={16}
                    color={embedCopyStatus.includes('Failed') ? '#e74c3c' : '#27ae60'}
                  />
                  <Text style={{
                    color: embedCopyStatus.includes('Failed') ? '#e74c3c' : '#27ae60',
                    fontSize: 14,
                    fontWeight: '500',
                    marginLeft: 8
                  }}>
                    {embedCopyStatus}
                  </Text>
                </View>
              ) : null}

              {/* Copy Embed Code Button */}
              <Button
                size="$4"
                backgroundColor="#6c5ce7"
                color="white"
                borderRadius={8}
                fontWeight="600"
                pressStyle={{ backgroundColor: "#5b4cdb" }}
                onPress={copyEmbedCode}
                icon={<Feather name="copy" size={18} color="white" />}
              >
                Copy Embed Code
              </Button>
            </YStack>
          )}
        </View>
      </ScrollView>
    </StyledModal>
  );
});

const stylesLocal = StyleSheet.create({
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#e8f4fd',
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#5ac0ea',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
      },
      android: {
        elevation: 6,
      },
      web: {
        boxShadow: '0 4px 20px rgba(90, 192, 234, 0.15)',
      }
    }),
  },
  qrContainer: {
    borderRadius: 16,
    padding: 20,
    alignItems: 'center',
    marginVertical: 16,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 16px rgba(0, 0, 0, 0.08)',
      }
    }),
  },
  qrCodeWrapper: {
    padding: 16,
    backgroundColor: 'white',
    borderRadius: 12,
    marginBottom: 16,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
      }
    }),
  },
  linkPreview: {
    width: '100%',
    paddingVertical: 12,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  infoSection: {
    marginTop: 24,
    padding: 16,
    backgroundColor: 'rgba(90, 192, 234, 0.05)',
    borderRadius: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#5ac0ea',
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 4,
  },
  infoText: {
    marginLeft: 12,
    fontSize: 14,
    color: '#6c757d',
    lineHeight: 20,
    flex: 1,
  },
  embedSection: {
    marginTop: 24,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(90, 192, 234, 0.2)',
  },
  labelText: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 6,
  },
  dimensionInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontWeight: '500',
  },
  codeContainer: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    maxHeight: 120,
    overflow: 'scroll' as any,
  },
  codeText: {
    fontFamily: Platform.OS === 'web' ? 'Consolas, Monaco, "Courier New", monospace' : 'System',
    fontSize: 12,
    lineHeight: 18,
    color: '#00d4ff',
  },
});

export default CalendarQRModal;